import { useEffect, useRef, useState } from 'react';

const reasons = [
  {
    id: 'gov',
    n: '01',
    title: 'Official UK Government Partner',
    short: 'UK Government partner',
    body: "We're the only medical-applications company to have worked directly with the UK Government and UK medical schools."
  },
  {
    id: 'reviews',
    n: '02',
    title: 'Ahead of the Competition',
    short: 'Ahead of the competition',
    body: 'Public review counts vs other UK admissions companies. The volume gap is the work.'
  },
  {
    id: 'avg',
    n: '03',
    title: '1.8× above the national average',
    short: '1.8× UK average',
    body: 'Students who booked 40+ hours of UCAT tuition with us in 2024 averaged 2810. The national cohort average sits around 2400.'
  },
  {
    id: 'guarantee',
    n: '04',
    title: '100% money-back guarantee',
    short: '100% money-back',
    body: 'We offer 100% money back on the first lesson, no questions asked. The risk of trying us is zero.'
  },
  {
    id: 'docs',
    n: '05',
    title: '10,000 NHS doctors since 2017',
    short: '10,000 NHS doctors',
    body: 'Our full application service — UCAT, work experience, interview, UCAS — has produced 10,000 students who are now NHS doctors and dentists.'
  },
  {
    id: 'fourfive',
    n: '06',
    title: '4 in 5 use Medic Mind',
    short: '4 in 5 applicants',
    body: 'Across the UK medical-applications market, 4 out of 5 students applying to medicine have used Medic Mind in some capacity.'
  },
  {
    id: 'support',
    n: '07',
    title: 'Your whole application support',
    short: 'Whole application support',
    body: "We don't just teach UCAT. Personal statement, work experience, interview prep, school choice — every step covered by the same team that's been doing it for 10 years."
  }
];

const partners = ['Walsall Council', 'Leicester City Council', 'Hillingdon LBC', 'Suffolk County Council', 'Staffordshire CC', 'Newcastle City Council', 'Bury Council', 'Nottingham City Council'];

// ===== hooks =====

function useInView() {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    if (!ref.current) return;
    const obs = new IntersectionObserver(([e]) => {
      if (e.isIntersecting) { setInView(true); obs.disconnect(); }
    }, { threshold: 0.15 });
    obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);
  return [ref, inView];
}

function useCountUp(target, { duration = 1400, start = false } = {}) {
  const [val, setVal] = useState(0);
  useEffect(() => {
    if (!start) { setVal(0); return; }
    let frame;
    const t0 = performance.now();
    const tick = (t) => {
      const p = Math.min(1, (t - t0) / duration);
      const eased = 1 - Math.pow(1 - p, 3);
      setVal(eased * target);
      if (p < 1) frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [target, duration, start]);
  return val;
}

// ===== component =====

export default function SevenReasons() {
  const [ref, inView] = useInView();
  const [openId, setOpenId] = useState('reviews');
  const [paused, setPaused] = useState(false);

  useEffect(() => {
    if (paused || !inView) return;
    const id = setInterval(() => {
      setOpenId((cur) => {
        const i = reasons.findIndex((r) => r.id === cur);
        return reasons[(i + 1) % reasons.length].id;
      });
    }, 7000);
    return () => clearInterval(id);
  }, [paused, inView]);

  const active = reasons.find((r) => r.id === openId);

  return (
    <section
      ref={ref}
      className="seven-reasons"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      style={{
        ...shell,
        opacity: inView ? 1 : 0,
        transform: inView ? 'translateY(0)' : 'translateY(24px)',
        transition: 'opacity 700ms ease, transform 700ms cubic-bezier(0.16, 1, 0.3, 1)'
      }}
    >
      <div style={headerRow}>
        <div>
          <div style={kicker}><Sparkle /> Why Medic Mind</div>
          <h2 style={title}>
            <span style={{ color: 'var(--brand-purple)' }}>7 reasons</span> we're the UK's top medical-applications team
          </h2>
        </div>
        <div style={progressTrack}>
          <div style={{
            ...progressFill,
            width: `${((reasons.findIndex((r) => r.id === openId) + 1) / reasons.length) * 100}%`
          }} />
        </div>
      </div>

      <div style={grid} className="sr-grid">
        {/* LEFT STAGE — changes with toggle */}
        <div style={leftStage}>
          <div style={leftOrbA} />
          <div style={leftOrbB} />
          <div style={dotPattern} />

          {reasons.map((r) => (
            <div
              key={r.id}
              style={{
                ...stageLayer,
                opacity: openId === r.id ? 1 : 0,
                transform: openId === r.id ? 'translateY(0)' : 'translateY(12px)',
                pointerEvents: openId === r.id ? 'auto' : 'none'
              }}
            >
              <Stage reason={r} active={openId === r.id} />
            </div>
          ))}
        </div>

        {/* RIGHT LIST */}
        <div style={rightList}>
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            {reasons.map((r, i) => (
              <ListRow
                key={r.id}
                reason={r}
                active={openId === r.id}
                onClick={() => setOpenId(r.id)}
                inView={inView}
                delay={i * 70}
              />
            ))}
          </div>

          <div style={{ marginTop: 22 }}>
            <div style={smallLabel}>Trusted partners</div>
            <div style={partnerWrap}>
              {partners.map((p, i) => (
                <span key={p} style={{
                  ...partnerChip,
                  opacity: inView ? 1 : 0,
                  transform: inView ? 'translateY(0)' : 'translateY(6px)',
                  transition: `opacity 380ms ease ${600 + i * 50}ms, transform 380ms ease ${600 + i * 50}ms`
                }}>{p}</span>
              ))}
            </div>
          </div>

          <div style={{ marginTop: 18 }}>
            <div style={smallLabel}>Awards & recognition</div>
            <div style={badgeRow}>
              {badges.map((b, i) => (
                <div
                  key={b.label}
                  className="sr-badge"
                  style={{
                    ...badge,
                    opacity: inView ? 1 : 0,
                    transform: inView ? 'translateY(0) scale(1)' : 'translateY(8px) scale(0.95)',
                    transition: `opacity 460ms ease ${700 + i * 90}ms, transform 460ms cubic-bezier(0.16, 1, 0.3, 1) ${700 + i * 90}ms`
                  }}
                  title={b.label}
                >
                  <b.Icon />
                  <span style={badgeLabel}>{b.label}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      <style>{`
        @media (max-width: 960px) {
          .sr-grid { grid-template-columns: 1fr !important; }
          .sr-left { min-height: 380px !important; }
        }
        @keyframes mm-orb-drift {
          0%, 100% { transform: translate(0, 0); }
          50% { transform: translate(18px, -14px); }
        }
        @keyframes mm-pulse-glow {
          0%, 100% { box-shadow: 0 0 20px rgba(168, 85, 247, 0.35); }
          50% { box-shadow: 0 0 40px rgba(168, 85, 247, 0.75); }
        }
        @keyframes mm-bob {
          0%, 100% { transform: translate(-50%, -2px); }
          50% { transform: translate(-50%, -10px); }
        }
        @keyframes mm-shimmer-line {
          0% { transform: translateX(-100%); opacity: 0; }
          40% { opacity: 1; }
          100% { transform: translateX(100%); opacity: 0; }
        }
        @keyframes mm-float-soft {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-4px); }
        }
        .sr-list-row { transition: background .25s ease, padding-left .25s ease, color .25s ease; }
        .sr-list-row:hover { background: rgba(255,255,255,0.05); padding-left: 18px; }
        .sr-list-row:hover .sr-row-num { color: #fff; }
        .sr-list-row:hover .sr-row-arrow { opacity: 1; transform: translateX(0); }
        .sr-badge { transition: transform .2s cubic-bezier(0.16, 1, 0.3, 1), background .2s ease, border-color .2s ease; }
        .sr-badge:hover { transform: translateY(-2px); background: rgba(255,255,255,0.1) !important; border-color: rgba(255,255,255,0.25) !important; }
      `}</style>
    </section>
  );
}

// ===== List row (right side) =====

function ListRow({ reason, active, onClick, inView, delay }) {
  return (
    <button
      onClick={onClick}
      className="sr-list-row"
      style={{
        ...listRow,
        background: active ? 'linear-gradient(90deg, rgba(168,85,247,0.18), rgba(168,85,247,0.04))' : 'transparent',
        borderLeft: `2px solid ${active ? 'var(--brand-purple)' : 'transparent'}`,
        paddingLeft: active ? 18 : 14,
        opacity: inView ? 1 : 0,
        transform: inView ? 'translateX(0)' : 'translateX(12px)',
        transitionDelay: `${delay}ms`
      }}
      aria-pressed={active}
    >
      <span className="sr-row-num" style={{ ...rowNum, color: active ? 'var(--brand-purple)' : 'rgba(255,255,255,0.4)' }}>{reason.n}</span>
      <span style={{
        flex: 1, color: active ? '#fff' : 'rgba(255,255,255,0.85)',
        fontSize: 14.5, fontWeight: active ? 600 : 500, textAlign: 'left',
        lineHeight: 1.35
      }}>{reason.title}</span>
      <ArrowGlyph className="sr-row-arrow" style={{
        opacity: active ? 1 : 0.3,
        transform: active ? 'translateX(0)' : 'translateX(-4px)',
        transition: 'opacity .2s ease, transform .2s ease'
      }} />
    </button>
  );
}

// ===== Stage (left side per reason) =====

function Stage({ reason, active }) {
  const Body = stages[reason.id];
  return (
    <div style={{ position: 'relative', zIndex: 1, display: 'flex', flexDirection: 'column', height: '100%', padding: 'clamp(24px, 3vw, 36px)' }}>
      <div style={stageHeadBlock}>
        <div style={stageNum}>{reason.n} / 07</div>
        <h3 style={stageTitle}>{reason.title}</h3>
        <p style={stageBody}>{reason.body}</p>
      </div>
      <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 0 }}>
        <Body active={active} />
      </div>
    </div>
  );
}

const stages = {
  gov: GovStage,
  reviews: ReviewsStage,
  avg: AvgStage,
  guarantee: GuaranteeStage,
  docs: DocsStage,
  fourfive: FourFiveStage,
  support: SupportStage
};

function GovStage({ active }) {
  return (
    <div style={{ width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 18 }}>
      <ParliamentBig active={active} />
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 7, justifyContent: 'center' }}>
        {['Walsall', 'Leicester', 'Hillingdon', 'Suffolk', 'Nottingham', 'Newcastle', 'UK Govt'].map((p, i) => (
          <span key={p} style={{
            padding: '6px 12px',
            background: p === 'UK Govt' ? 'rgba(168, 85, 247, 0.28)' : 'rgba(255,255,255,0.08)',
            border: `1px solid ${p === 'UK Govt' ? 'rgba(168, 85, 247, 0.55)' : 'rgba(255,255,255,0.18)'}`,
            borderRadius: 99, fontSize: 12, color: '#fff', fontWeight: 500,
            opacity: active ? 1 : 0,
            transform: active ? 'scale(1)' : 'scale(0.85)',
            transition: `opacity 360ms ease ${i * 70}ms, transform 360ms cubic-bezier(0.16, 1, 0.3, 1) ${i * 70}ms`
          }}>{p}</span>
        ))}
      </div>
    </div>
  );
}

function ReviewsStage({ active }) {
  const data = [
    { label: 'Medic Mind', value: 2000, brand: true, Logo: LogoMM },
    { label: 'Medichut', value: 800, Logo: LogoMedichut },
    { label: 'Superprof', value: 400, Logo: LogoSuperprof },
    { label: '6med', value: 200, Logo: Logo6med },
    { label: 'The Medic Portal', value: 100, Logo: LogoMedicPortal }
  ];
  const max = 2000;
  return (
    <div style={{ width: '100%' }}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {data.map((d, i) => (
          <ReviewsRow key={d.label} d={d} i={i} max={max} active={active} />
        ))}
      </div>
      <div style={{ marginTop: 14, display: 'flex', gap: 16, fontSize: 11.5, color: 'rgba(255,255,255,0.65)' }}>
        <Legend swatch="rgba(255,255,255,0.22)" label="Other UK companies" />
        <Legend swatch="var(--brand-purple)" label="Medic Mind" />
      </div>
    </div>
  );
}

function ReviewsRow({ d, i, max, active }) {
  const w = (d.value / max) * 100;
  const count = useCountUp(d.value, { start: active, duration: 1100 + i * 80 });
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
      <div style={{
        width: 80, flexShrink: 0,
        display: 'flex', alignItems: 'center', gap: 6,
        opacity: active ? 1 : 0,
        transform: active ? 'translateX(0)' : 'translateX(-6px)',
        transition: `opacity 380ms ease ${i * 80}ms, transform 380ms ease ${i * 80}ms`
      }}>
        <d.Logo />
      </div>
      <div style={{ flex: 1, position: 'relative', height: 26 }}>
        <div style={{
          position: 'absolute', left: 0, top: 0, bottom: 0,
          width: active ? `${w}%` : '0%',
          borderRadius: 6,
          background: d.brand
            ? 'linear-gradient(90deg, var(--brand-purple), #c084fc)'
            : 'rgba(255,255,255,0.22)',
          transition: `width 1100ms cubic-bezier(0.16, 1, 0.3, 1) ${i * 80}ms`,
          overflow: 'hidden',
          ...(d.brand && active ? { boxShadow: '0 0 24px rgba(168, 85, 247, 0.55)' } : {})
        }}>
          {d.brand && active && (
            <div style={{
              position: 'absolute', top: 0, bottom: 0, width: '40%',
              background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.4), transparent)',
              animation: 'mm-shimmer-line 2.4s ease-in-out infinite',
              animationDelay: `${1200 + i * 80}ms`
            }} />
          )}
        </div>
        {d.brand && (
          <CrownIcon style={{
            position: 'absolute', top: -16, left: `${w}%`,
            transform: 'translate(-50%, -2px)',
            animation: active ? 'mm-bob 2.2s ease-in-out infinite' : 'none',
            opacity: active ? 1 : 0, transition: 'opacity 600ms ease 1000ms', zIndex: 2
          }} />
        )}
      </div>
      <div style={{
        width: 56, textAlign: 'right', fontSize: 13,
        color: d.brand ? '#fff' : 'rgba(255,255,255,0.7)',
        fontWeight: d.brand ? 700 : 500,
        fontFamily: 'Montserrat, sans-serif',
        fontVariantNumeric: 'tabular-nums'
      }}>
        {Math.round(count).toLocaleString()}
      </div>
    </div>
  );
}

function AvgStage({ active }) {
  const mult = useCountUp(1.8, { start: active, duration: 1500 });
  return (
    <div style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 26, flexWrap: 'wrap', justifyContent: 'center' }}>
      <div style={{
        fontSize: 'clamp(72px, 12vw, 110px)', fontWeight: 700,
        fontFamily: 'Montserrat, sans-serif', color: '#fff',
        letterSpacing: '-0.04em', lineHeight: 0.9,
        textShadow: '0 0 40px rgba(168, 85, 247, 0.55)'
      }}>
        {mult.toFixed(1)}<span style={{ color: 'var(--brand-purple)' }}>×</span>
      </div>
      <div style={{ display: 'flex', alignItems: 'flex-end', gap: 26, height: 200 }}>
        <UcatCol value={640} label="UK average" active={active} i={0} />
        <UcatCol value={720} label="Our students" brand winner active={active} i={1} />
      </div>
    </div>
  );
}

function UcatCol({ value, label, brand, winner, active, i }) {
  const h = ((value - 300) / (900 - 300)) * 100;
  const count = useCountUp(value, { start: active, duration: 1300 + i * 200 });
  return (
    <div style={{ width: 80, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6 }}>
      <div style={{ fontSize: 14, fontWeight: 600, color: 'rgba(255,255,255,0.95)', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
        {winner && <TrophySmall />}{Math.round(count)}
      </div>
      <div style={{ width: '100%', flex: 1, display: 'flex', alignItems: 'flex-end' }}>
        <div style={{
          width: '100%',
          height: active ? `${h}%` : '0%',
          borderRadius: '6px 6px 0 0',
          transition: `height 1100ms cubic-bezier(0.16, 1, 0.3, 1) ${i * 200}ms`,
          background: brand ? 'linear-gradient(180deg, #c084fc, var(--brand-purple))' : 'rgba(255,255,255,0.22)',
          ...(brand ? { boxShadow: '0 0 28px rgba(168, 85, 247, 0.5)' } : {})
        }} />
      </div>
      <div style={{ fontSize: 11.5, color: 'rgba(255,255,255,0.7)', textAlign: 'center' }}>{label}</div>
    </div>
  );
}

function GuaranteeStage({ active }) {
  const pct = useCountUp(100, { start: active, duration: 1200 });
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 24, flexWrap: 'wrap', justifyContent: 'center' }}>
      <div style={{ position: 'relative', width: 180, height: 180, display: 'grid', placeItems: 'center' }}>
        <ShieldBig active={active} />
        <div style={{
          position: 'relative', zIndex: 1,
          fontSize: 48, fontWeight: 700, color: '#fff',
          fontFamily: 'Montserrat, sans-serif', letterSpacing: '-0.03em',
          textShadow: '0 4px 14px rgba(0,0,0,0.35)'
        }}>{Math.round(pct)}<span style={{ fontSize: 28 }}>%</span></div>
      </div>
      <div style={{
        flex: 1, minWidth: 200, maxWidth: 280,
        color: 'rgba(255,255,255,0.85)', fontSize: 14.5, lineHeight: 1.55
      }}>
        <div style={{ color: '#fff', fontWeight: 600, fontSize: 16, marginBottom: 6 }}>Money back on lesson 1</div>
        Try a Medic Mind tutor. If we're not the right fit, you walk with a full refund. No questions, no escape clauses.
      </div>
    </div>
  );
}

function DocsStage({ active }) {
  const total = useCountUp(10000, { start: active, duration: 1800 });
  const cols = 10, rows = 5;
  return (
    <div style={{ width: '100%' }}>
      <div style={{
        textAlign: 'center', marginBottom: 14,
        fontSize: 36, fontWeight: 700, fontFamily: 'Montserrat, sans-serif',
        color: '#fff', letterSpacing: '-0.02em',
        textShadow: '0 0 30px rgba(168, 85, 247, 0.45)'
      }}>
        <span style={{ color: 'var(--brand-purple)' }}>{Math.round(total).toLocaleString()}+</span>
        <span style={{ fontSize: 18, color: 'rgba(255,255,255,0.7)', fontWeight: 500, marginLeft: 8 }}>NHS doctors since 2017</span>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: `repeat(${cols}, 1fr)`, gap: 8, maxWidth: 380, margin: '0 auto' }}>
        {Array.from({ length: cols * rows }).map((_, i) => (
          <div key={i} style={{
            aspectRatio: '1 / 1',
            display: 'grid', placeItems: 'center',
            opacity: active ? 1 : 0,
            transform: active ? 'scale(1)' : 'scale(0.4)',
            transition: `opacity 320ms ease ${i * 16}ms, transform 320ms cubic-bezier(0.16, 1, 0.3, 1) ${i * 16}ms`
          }}>
            <DoctorIcon />
          </div>
        ))}
      </div>
      <div style={{ marginTop: 12, textAlign: 'center', color: 'rgba(255,255,255,0.6)', fontSize: 12 }}>Each silhouette ≈ 200 doctors</div>
    </div>
  );
}

function FourFiveStage({ active }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 18, width: '100%' }}>
      <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', justifyContent: 'center' }}>
        {[1, 2, 3, 4, 5].map((i) => (
          <div key={i} style={{
            width: 76, height: 76, borderRadius: 16,
            background: i <= 4 ? 'linear-gradient(135deg, #c084fc, var(--brand-purple))' : 'rgba(255,255,255,0.06)',
            border: i <= 4 ? 'none' : '1.5px dashed rgba(255,255,255,0.25)',
            display: 'grid', placeItems: 'center',
            boxShadow: i <= 4 ? '0 12px 28px rgba(168, 85, 247, 0.35)' : 'none',
            opacity: active ? 1 : 0,
            transform: active ? 'translateY(0) scale(1)' : 'translateY(8px) scale(0.85)',
            transition: `opacity 400ms ease ${i * 100}ms, transform 400ms cubic-bezier(0.16, 1, 0.3, 1) ${i * 100}ms`,
            animation: i <= 4 && active ? `mm-float-soft 4s ease-in-out infinite ${i * 0.3}s` : 'none'
          }}>
            <PersonIcon faded={i === 5} large />
          </div>
        ))}
      </div>
      <div style={{
        fontSize: 28, fontWeight: 700, fontFamily: 'Montserrat, sans-serif',
        color: '#fff', letterSpacing: '-0.01em'
      }}>
        <span style={{ color: 'var(--brand-purple)' }}>4 in 5</span> medicine applicants
      </div>
      <div style={{ fontSize: 13.5, color: 'rgba(255,255,255,0.7)', textAlign: 'center', maxWidth: 360 }}>
        Drills, tutoring, Career Camp or the free portal — most of the UK cohort touches a Medic Mind product.
      </div>
    </div>
  );
}

function SupportStage({ active }) {
  const items = ['UCAT prep', 'Personal statement', 'Work experience', 'Interview prep', 'School choice', 'Reapplication', 'Graduate-entry'];
  return (
    <div style={{ width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 20 }}>
      <div style={{ position: 'relative', width: 200, height: 200 }}>
        <SupportHub active={active} />
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, justifyContent: 'center', maxWidth: 380 }}>
        {items.map((s, i) => (
          <div key={s} style={{
            padding: '8px 14px',
            background: 'linear-gradient(135deg, rgba(255,255,255,0.12), rgba(255,255,255,0.04))',
            border: '1px solid rgba(255,255,255,0.18)',
            borderRadius: 99, fontSize: 12.5, color: '#fff', fontWeight: 500,
            opacity: active ? 1 : 0,
            transform: active ? 'translateY(0)' : 'translateY(6px)',
            transition: `opacity 420ms ease ${600 + i * 70}ms, transform 420ms cubic-bezier(0.16, 1, 0.3, 1) ${600 + i * 70}ms`
          }}>{s}</div>
        ))}
      </div>
    </div>
  );
}

// ===== SVGs =====

function Sparkle() {
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
      <path d="M7 0.5 L8.5 5.5 L13.5 7 L8.5 8.5 L7 13.5 L5.5 8.5 L0.5 7 L5.5 5.5 Z" fill="currentColor" />
    </svg>
  );
}

function ArrowGlyph({ style, className }) {
  return (
    <svg className={className} width="14" height="14" viewBox="0 0 14 14" fill="none" style={style}>
      <path d="M3 7 L10 7 M7 4 L10 7 L7 10" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function CrownIcon({ style }) {
  return (
    <svg width="22" height="16" viewBox="0 0 22 16" fill="none" style={style}>
      <path d="M2 13 L4 4 L8 9 L11 2 L14 9 L18 4 L20 13 Z" fill="#fbbf24" stroke="#f59e0b" strokeWidth="0.8" strokeLinejoin="round" />
      <circle cx="4" cy="4" r="1.2" fill="#fef3c7" />
      <circle cx="11" cy="2" r="1.2" fill="#fef3c7" />
      <circle cx="18" cy="4" r="1.2" fill="#fef3c7" />
    </svg>
  );
}

function TrophySmall() {
  return (
    <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
      <path d="M5 2 L11 2 L11 7 Q11 10 8 10 Q5 10 5 7 Z" fill="#fbbf24" />
      <rect x="6.5" y="10" width="3" height="2.5" fill="#fbbf24" />
      <rect x="4.5" y="12" width="7" height="1.5" rx="0.5" fill="#fbbf24" />
    </svg>
  );
}

function ParliamentBig({ active }) {
  return (
    <svg width="220" height="160" viewBox="0 0 220 160" fill="none" style={{
      opacity: active ? 1 : 0,
      transform: active ? 'scale(1) translateY(0)' : 'scale(0.85) translateY(8px)',
      transition: 'opacity 600ms ease, transform 600ms cubic-bezier(0.16, 1, 0.3, 1)',
      filter: 'drop-shadow(0 12px 30px rgba(168, 85, 247, 0.35))'
    }}>
      <defs>
        <linearGradient id="parBigGrad" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#fff" stopOpacity="0.95" />
          <stop offset="100%" stopColor="#fff" stopOpacity="0.6" />
        </linearGradient>
      </defs>
      {/* base */}
      <rect x="20" y="100" width="180" height="50" fill="url(#parBigGrad)" rx="2" />
      {/* central tower */}
      <rect x="95" y="60" width="30" height="40" fill="url(#parBigGrad)" />
      <path d="M110 30 L130 60 L90 60 Z" fill="url(#parBigGrad)" />
      <circle cx="110" cy="50" r="5" fill="var(--brand-purple)" />
      <line x1="110" y1="20" x2="110" y2="30" stroke="rgba(255,255,255,0.85)" strokeWidth="1.5" />
      <circle cx="110" cy="18" r="2.5" fill="#fbbf24" />
      {/* side towers */}
      <rect x="40" y="76" width="22" height="24" fill="url(#parBigGrad)" />
      <path d="M51 60 L66 76 L36 76 Z" fill="url(#parBigGrad)" />
      <rect x="158" y="76" width="22" height="24" fill="url(#parBigGrad)" />
      <path d="M169 60 L184 76 L154 76 Z" fill="url(#parBigGrad)" />
      {/* columns */}
      {[28, 44, 60, 76, 92, 108, 124, 140, 156, 172, 188].map((x, i) => (
        <rect key={x} x={x} y="108" width="6" height="32" fill="var(--brand-deep)"
          opacity={active ? 1 : 0}
          style={{ transition: `opacity 380ms ease ${200 + i * 40}ms` }}
        />
      ))}
      {/* ground line */}
      <rect x="14" y="148" width="192" height="3" fill="rgba(255,255,255,0.5)" rx="1.5" />
    </svg>
  );
}

function ShieldBig({ active }) {
  return (
    <svg width="180" height="180" viewBox="0 0 180 180" fill="none" style={{ position: 'absolute', inset: 0, overflow: 'visible' }}>
      <defs>
        <linearGradient id="shieldBig" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#c084fc" />
          <stop offset="100%" stopColor="var(--brand-purple)" />
        </linearGradient>
      </defs>
      <path
        d="M90 14 L154 36 L154 86 Q154 132 90 168 Q26 132 26 86 L26 36 Z"
        fill="url(#shieldBig)"
        opacity={active ? 1 : 0.3}
        style={{
          transition: 'opacity 600ms ease',
          animation: active ? 'mm-pulse-glow 2.8s ease-in-out infinite' : 'none'
        }}
      />
      <path
        d="M90 14 L154 36 L154 86 Q154 132 90 168 Q26 132 26 86 L26 36 Z"
        fill="none" stroke="rgba(255,255,255,0.4)" strokeWidth="2"
      />
      <path
        d="M58 86 L82 110 L126 64"
        stroke="rgba(255,255,255,0.55)" strokeWidth="5"
        strokeLinecap="round" strokeLinejoin="round" fill="none"
        opacity={active ? 0.45 : 0}
        style={{ transition: 'opacity 800ms ease 300ms' }}
      />
    </svg>
  );
}

function SupportHub({ active }) {
  const nodes = [
    { angle: 0 }, { angle: 51 }, { angle: 103 }, { angle: 154 },
    { angle: 206 }, { angle: 257 }, { angle: 309 }
  ];
  const R = 78, cx = 100, cy = 100;
  return (
    <svg width="200" height="200" viewBox="0 0 200 200" fill="none" style={{ overflow: 'visible' }}>
      {/* connectors */}
      {nodes.map((n, i) => {
        const rad = (n.angle - 90) * Math.PI / 180;
        const x = cx + R * Math.cos(rad);
        const y = cy + R * Math.sin(rad);
        return (
          <line key={i} x1={cx} y1={cy} x2={x} y2={y}
            stroke="rgba(168, 85, 247, 0.45)" strokeWidth="1.5"
            strokeDasharray="200"
            strokeDashoffset={active ? 0 : 200}
            style={{ transition: `stroke-dashoffset 700ms cubic-bezier(0.16, 1, 0.3, 1) ${300 + i * 80}ms` }}
          />
        );
      })}
      {/* outer dots */}
      {nodes.map((n, i) => {
        const rad = (n.angle - 90) * Math.PI / 180;
        const x = cx + R * Math.cos(rad);
        const y = cy + R * Math.sin(rad);
        return (
          <circle key={i} cx={x} cy={y} r="10"
            fill="rgba(255,255,255,0.95)"
            opacity={active ? 1 : 0}
            style={{
              transition: `opacity 380ms ease ${500 + i * 80}ms`,
              filter: 'drop-shadow(0 0 6px rgba(168, 85, 247, 0.5))'
            }}
          />
        );
      })}
      {/* center */}
      <circle cx={cx} cy={cy} r="28"
        fill="url(#hubGrad)"
        opacity={active ? 1 : 0}
        style={{ transition: 'opacity 500ms ease 200ms' }}
      />
      <circle cx={cx} cy={cy} r="28" fill="none" stroke="rgba(255,255,255,0.4)" strokeWidth="1.5" opacity={active ? 1 : 0} style={{ transition: 'opacity 500ms ease 200ms' }} />
      <defs>
        <radialGradient id="hubGrad">
          <stop offset="0%" stopColor="#c084fc" />
          <stop offset="100%" stopColor="var(--brand-purple)" />
        </radialGradient>
      </defs>
      <text x={cx} y={cy + 5} textAnchor="middle" fill="#fff" fontSize="12" fontWeight="700" fontFamily="Montserrat, sans-serif" opacity={active ? 1 : 0} style={{ transition: 'opacity 500ms ease 400ms' }}>MEDIC</text>
      <text x={cx} y={cy + 18} textAnchor="middle" fill="#fff" fontSize="12" fontWeight="700" fontFamily="Montserrat, sans-serif" opacity={active ? 1 : 0} style={{ transition: 'opacity 500ms ease 400ms' }}>MIND</text>
    </svg>
  );
}

function DoctorIcon() {
  return (
    <svg width="100%" height="100%" viewBox="0 0 32 36" fill="none">
      <circle cx="16" cy="9" r="5.5" fill="var(--brand-purple)" />
      <path d="M4 36 Q4 19 16 19 Q28 19 28 36 Z" fill="var(--brand-purple)" />
      <rect x="14" y="19" width="4" height="6" fill="#fff" opacity="0.85" />
      <circle cx="11" cy="26" r="1.4" fill="#fff" opacity="0.8" />
      <path d="M11 26 Q14 28 16 30" stroke="#fff" strokeWidth="0.9" fill="none" opacity="0.7" />
      <circle cx="20" cy="30" r="1.4" fill="#fff" opacity="0.8" />
    </svg>
  );
}

function PersonIcon({ faded, large }) {
  const s = large ? 40 : 30;
  return (
    <svg width={s} height={s} viewBox="0 0 30 30" fill="none">
      <circle cx="15" cy="10" r="5" fill={faded ? 'rgba(255,255,255,0.3)' : '#fff'} />
      <path d="M3 30 Q3 18 15 18 Q27 18 27 30" fill={faded ? 'rgba(255,255,255,0.3)' : '#fff'} />
    </svg>
  );
}

function Legend({ swatch, label }) {
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
      <span style={{ width: 10, height: 10, borderRadius: 3, background: swatch, display: 'inline-block' }} />
      {label}
    </span>
  );
}

// ===== Brand logos (stylised, on dark background) =====

function LogoMM() {
  return (
    <svg width="76" height="22" viewBox="0 0 76 22" fill="none">
      <circle cx="11" cy="11" r="10" fill="#fff" />
      <text x="11" y="15" textAnchor="middle" fill="var(--brand-purple)" fontFamily="Montserrat, sans-serif" fontSize="11" fontWeight="700">MM</text>
      <text x="26" y="15" fill="#fff" fontFamily="Montserrat, sans-serif" fontSize="10.5" fontWeight="600" letterSpacing="0.02em">Medic Mind</text>
    </svg>
  );
}

function LogoMedichut() {
  return (
    <svg width="76" height="22" viewBox="0 0 76 22" fill="none">
      <path d="M3 14 L12 5 L21 14 L21 19 L3 19 Z" fill="#10b981" />
      <rect x="9" y="13" width="6" height="6" fill="#065f46" />
      <text x="26" y="15" fill="rgba(255,255,255,0.75)" fontFamily="Montserrat, sans-serif" fontSize="10.5" fontWeight="500">Medichut</text>
    </svg>
  );
}

function LogoSuperprof() {
  return (
    <svg width="76" height="22" viewBox="0 0 76 22" fill="none">
      <rect x="2" y="2" width="18" height="18" rx="4" fill="#fbbf24" />
      <text x="11" y="15" textAnchor="middle" fill="#000" fontFamily="Montserrat, sans-serif" fontSize="11" fontWeight="700">S</text>
      <text x="25" y="15" fill="rgba(255,255,255,0.75)" fontFamily="Montserrat, sans-serif" fontSize="10.5" fontWeight="500">Superprof</text>
    </svg>
  );
}

function Logo6med() {
  return (
    <svg width="76" height="22" viewBox="0 0 76 22" fill="none">
      <circle cx="11" cy="11" r="10" fill="#0ea5e9" />
      <text x="11" y="15" textAnchor="middle" fill="#fff" fontFamily="Montserrat, sans-serif" fontSize="11" fontWeight="700">6</text>
      <text x="26" y="15" fill="rgba(255,255,255,0.75)" fontFamily="Montserrat, sans-serif" fontSize="10.5" fontWeight="500">6med</text>
    </svg>
  );
}

function LogoMedicPortal() {
  return (
    <svg width="76" height="22" viewBox="0 0 76 22" fill="none">
      <circle cx="11" cy="11" r="10" fill="#ef4444" />
      <path d="M11 5 L11 17 M5 11 L17 11" stroke="#fff" strokeWidth="2.2" strokeLinecap="round" />
      <text x="26" y="11" fill="rgba(255,255,255,0.75)" fontFamily="Montserrat, sans-serif" fontSize="8" fontWeight="500">The Medic</text>
      <text x="26" y="19" fill="rgba(255,255,255,0.75)" fontFamily="Montserrat, sans-serif" fontSize="8" fontWeight="500">Portal</text>
    </svg>
  );
}

// ===== Badges (right side) =====

function ParliamentBadge() {
  return (
    <svg width="22" height="22" viewBox="0 0 22 22" fill="none">
      <rect x="3" y="12" width="16" height="7" fill="#fbbf24" />
      <path d="M11 4 L17 11 L5 11 Z" fill="#fbbf24" />
      <rect x="5" y="14" width="2" height="4" fill="var(--brand-deep)" />
      <rect x="10" y="14" width="2" height="4" fill="var(--brand-deep)" />
      <rect x="15" y="14" width="2" height="4" fill="var(--brand-deep)" />
    </svg>
  );
}
function TrophyBadge() {
  return (
    <svg width="22" height="22" viewBox="0 0 22 22" fill="none">
      <path d="M7 3 L15 3 L15 9 Q15 13 11 13 Q7 13 7 9 Z" fill="#fbbf24" />
      <path d="M7 5 L4 5 Q3 5 3 6.5 Q3 8.5 5 9.5 L7 10" stroke="#fbbf24" strokeWidth="1.3" fill="none" />
      <path d="M15 5 L18 5 Q19 5 19 6.5 Q19 8.5 17 9.5 L15 10" stroke="#fbbf24" strokeWidth="1.3" fill="none" />
      <rect x="9" y="13" width="4" height="3" fill="#fbbf24" />
      <rect x="6" y="16" width="10" height="2" rx="0.6" fill="#fbbf24" />
    </svg>
  );
}
function StarBadge() {
  return (
    <svg width="22" height="22" viewBox="0 0 22 22" fill="none">
      <path d="M11 2 L13.6 8 L20 8.6 L15 13 L16.5 19 L11 16 L5.5 19 L7 13 L2 8.6 L8.4 8 Z" fill="#fbbf24" />
    </svg>
  );
}
function RocketBadge() {
  return (
    <svg width="22" height="22" viewBox="0 0 22 22" fill="none">
      <path d="M11 2 Q15 6 15 12 L15 16 L7 16 L7 12 Q7 6 11 2 Z" fill="#fbbf24" />
      <circle cx="11" cy="9" r="1.6" fill="var(--brand-purple)" />
      <path d="M7 16 L5 20 L8 18 Z" fill="#f59e0b" />
      <path d="M15 16 L17 20 L14 18 Z" fill="#f59e0b" />
    </svg>
  );
}

const badges = [
  { label: 'UK Govt Contract', Icon: ParliamentBadge },
  { label: 'Best Online Tuition 2024', Icon: TrophyBadge },
  { label: 'Top 5 Educational', Icon: StarBadge },
  { label: 'Best Medical Start Up', Icon: RocketBadge }
];

// ===== styles =====

const shell = {
  borderRadius: 22, overflow: 'hidden',
  background: 'linear-gradient(135deg, #1a0e30 0%, var(--brand-deep) 50%, #4c1d95 100%)',
  boxShadow: '0 30px 80px rgba(35, 20, 63, 0.35)',
  marginBottom: 28, position: 'relative',
  padding: 'clamp(20px, 2.5vw, 30px)',
  color: '#fff'
};

const headerRow = {
  display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end',
  gap: 16, marginBottom: 22, flexWrap: 'wrap',
  position: 'relative', zIndex: 1
};

const kicker = {
  display: 'inline-flex', alignItems: 'center', gap: 6,
  fontSize: 11.5, fontWeight: 600,
  textTransform: 'uppercase', letterSpacing: '0.08em',
  color: '#c084fc', marginBottom: 6,
  whiteSpace: 'nowrap', flexShrink: 0
};

const title = {
  margin: 0, fontFamily: 'Montserrat, sans-serif',
  fontSize: 'clamp(22px, 3vw, 30px)', fontWeight: 600,
  letterSpacing: '-0.01em', color: '#fff', lineHeight: 1.2,
  maxWidth: 560
};

const progressTrack = {
  width: 180, height: 4, borderRadius: 99,
  background: 'rgba(255,255,255,0.1)',
  overflow: 'hidden', flexShrink: 0
};
const progressFill = {
  height: '100%', background: 'linear-gradient(90deg, #c084fc, var(--brand-purple))',
  borderRadius: 99,
  transition: 'width 600ms cubic-bezier(0.16, 1, 0.3, 1)',
  boxShadow: '0 0 10px rgba(168, 85, 247, 0.6)'
};

const grid = {
  display: 'grid', gridTemplateColumns: '1.05fr 1fr',
  gap: 18, position: 'relative', zIndex: 1
};

const leftStage = {
  position: 'relative', borderRadius: 18,
  background: 'linear-gradient(135deg, rgba(255,255,255,0.04), rgba(255,255,255,0.01))',
  border: '1px solid rgba(255,255,255,0.08)',
  overflow: 'hidden',
  minHeight: 480, display: 'flex'
};
const stageLayer = {
  position: 'absolute', inset: 0,
  display: 'flex', flexDirection: 'column',
  transition: 'opacity 500ms cubic-bezier(0.16, 1, 0.3, 1), transform 500ms cubic-bezier(0.16, 1, 0.3, 1)'
};

const leftOrbA = {
  position: 'absolute', top: -60, right: -50, width: 220, height: 220, borderRadius: '50%',
  background: 'radial-gradient(circle, rgba(168, 85, 247, 0.4), transparent 70%)',
  pointerEvents: 'none', animation: 'mm-orb-drift 9s ease-in-out infinite'
};
const leftOrbB = {
  position: 'absolute', bottom: -50, left: -40, width: 200, height: 200, borderRadius: '50%',
  background: 'radial-gradient(circle, rgba(124, 58, 237, 0.3), transparent 70%)',
  pointerEvents: 'none', animation: 'mm-orb-drift 12s ease-in-out infinite reverse'
};
const dotPattern = {
  position: 'absolute', inset: 0,
  backgroundImage: 'radial-gradient(rgba(255,255,255,0.05) 1px, transparent 1px)',
  backgroundSize: '20px 20px',
  pointerEvents: 'none', opacity: 0.7
};

const stageHeadBlock = { marginBottom: 12 };
const stageNum = {
  fontSize: 11, fontWeight: 700, letterSpacing: '0.16em',
  color: 'rgba(255,255,255,0.5)', textTransform: 'uppercase', marginBottom: 6
};
const stageTitle = {
  margin: '0 0 6px', fontFamily: 'Montserrat, sans-serif',
  fontSize: 'clamp(20px, 2.5vw, 26px)', fontWeight: 600,
  letterSpacing: '-0.01em', color: '#fff', lineHeight: 1.2
};
const stageBody = {
  margin: 0, color: 'rgba(255,255,255,0.7)', fontSize: 14, lineHeight: 1.55
};

const rightList = { display: 'flex', flexDirection: 'column' };

const listRow = {
  display: 'flex', alignItems: 'center', gap: 12,
  padding: '14px 14px',
  border: 'none', borderLeft: '2px solid transparent',
  background: 'transparent', cursor: 'pointer',
  fontFamily: 'inherit', borderRadius: '0 10px 10px 0',
  transitionProperty: 'background, opacity, transform, padding-left, color',
  transitionDuration: '300ms',
  transitionTimingFunction: 'cubic-bezier(0.16, 1, 0.3, 1)'
};
const rowNum = {
  fontSize: 11, fontWeight: 700, letterSpacing: '0.1em',
  fontFamily: 'Montserrat, sans-serif',
  flexShrink: 0, transition: 'color .25s ease'
};

const smallLabel = {
  fontSize: 10.5, fontWeight: 700, letterSpacing: '0.14em',
  color: 'rgba(255,255,255,0.45)', textTransform: 'uppercase',
  marginBottom: 8
};

const partnerWrap = { display: 'flex', flexWrap: 'wrap', gap: 6 };
const partnerChip = {
  padding: '5px 11px',
  background: 'rgba(255,255,255,0.05)',
  border: '1px solid rgba(255,255,255,0.1)',
  borderRadius: 99, fontSize: 11.5,
  color: 'rgba(255,255,255,0.75)', fontWeight: 500
};

const badgeRow = { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: 8 };
const badge = {
  display: 'flex', alignItems: 'center', gap: 8,
  padding: '10px 12px',
  background: 'rgba(255,255,255,0.05)',
  border: '1px solid rgba(255,255,255,0.12)',
  borderRadius: 10,
  cursor: 'default'
};
const badgeLabel = {
  fontSize: 10.5, fontWeight: 600, color: '#fff',
  letterSpacing: '0.02em', lineHeight: 1.3
};
